import { useEffect, useState, type FormEvent } from "react";
import { useParams } from "react-router-dom";
import { api, type IntegrationRow } from "../api.ts";
import { StatusDot } from "../components/StatusDot.tsx";
import { fmtDateTime } from "../lib/format.ts";

const TYPE_LABEL: Record<string, string> = {
  iset: "iSET", erp: "ERP", matomo: "Matomo",
  search_console: "Google Search Console", ga4: "GA4",
  google_ads: "Google Ads", meta_ads: "Meta Ads",
};

type Field = { key: string; label: string; type?: string; hint?: string };

// ERP é genérico: um provider por loja. Cada provider tem seu próprio conjunto de campos.
const ERP_PROVIDERS: Array<{ value: string; label: string }> = [
  { value: "omie", label: "OMIE" },
  { value: "bling", label: "Bling" },
];
const ERP_PROVIDER_FIELDS: Record<string, Field[]> = {
  omie: [
    { key: "app_key", label: "App Key" },
    { key: "app_secret", label: "App Secret", type: "password" },
  ],
  bling: [
    { key: "client_id", label: "Client ID" },
    { key: "client_secret", label: "Client Secret", type: "password" },
    { key: "refresh_token", label: "Refresh Token", type: "password", hint: "OAuth2 Bling API v3" },
  ],
};

// Schema das credenciais por tipo (UI gera o form a partir disso)
const CONFIG_FIELDS: Record<string, Field[]> = {
  iset: [
    { key: "domain", label: "Domínio da loja", hint: "ex: lojanerea.com.br" },
    { key: "api_user", label: "API User" },
    { key: "api_key", label: "API Key", type: "password" },
  ],
  matomo: [
    { key: "url", label: "URL Matomo", hint: "https://stats.exemplo.com" },
    { key: "site_id", label: "Site ID", type: "number" },
    { key: "token_auth", label: "token_auth", type: "password" },
  ],
  search_console: [
    { key: "client_id", label: "OAuth Client ID" },
    { key: "client_secret", label: "OAuth Client Secret", type: "password" },
    { key: "refresh_token", label: "Refresh Token", type: "password" },
    { key: "site_url", label: "Site URL", hint: "sc-domain:exemplo.com.br OU https://www.exemplo.com.br/" },
  ],
  ga4: [
    { key: "client_id", label: "OAuth Client ID" },
    { key: "client_secret", label: "OAuth Client Secret", type: "password" },
    { key: "refresh_token", label: "Refresh Token", type: "password" },
    { key: "property_id", label: "GA4 Property ID" },
  ],
  google_ads: [
    { key: "client_id", label: "OAuth Client ID" },
    { key: "client_secret", label: "OAuth Client Secret", type: "password" },
    { key: "refresh_token", label: "Refresh Token", type: "password" },
    { key: "developer_token", label: "Developer Token", type: "password" },
    { key: "customer_id", label: "Customer ID", hint: "sem hífens, 10 dígitos" },
    { key: "login_customer_id", label: "Login Customer ID (MCC, opcional)" },
  ],
  meta_ads: [
    { key: "access_token", label: "Long-lived Access Token", type: "password" },
    { key: "ad_account_id", label: "Ad Account ID", hint: "sem o prefixo act_" },
  ],
};

function fieldsFor(type: string, form: Record<string, string>): Field[] {
  if (type === "erp") return ERP_PROVIDER_FIELDS[form.provider || "omie"] ?? [];
  return CONFIG_FIELDS[type] ?? [];
}

export function IntegrationsSettingsPage() {
  const { slug } = useParams<{ slug: string }>();
  const [list, setList] = useState<{ configured: IntegrationRow[]; available: string[] } | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [form, setForm] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  const load = async () => {
    setList(await api.get(`/api/t/${slug}/integrations`));
  };
  useEffect(() => { load(); }, [slug]);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!editing) return;
    setBusy(true);
    setMsg(null);
    try {
      const fields = fieldsFor(editing, form);
      const config: Record<string, any> = {};
      if (editing === "erp") config.provider = form.provider || "omie";
      for (const f of fields) {
        if (f.type === "number") config[f.key] = Number(form[f.key] ?? 0);
        else if (form[f.key]) config[f.key] = form[f.key];
      }
      await api.post(`/api/t/${slug}/integrations`, { type: editing, config });
      setEditing(null);
      setForm({});
      setMsg("salvo");
      load();
    } catch (err: any) {
      setMsg(`erro: ${err.message}`);
    } finally {
      setBusy(false);
    }
  };

  const runNow = async (id: number) => {
    await api.post(`/api/t/${slug}/integrations/${id}/run`);
    setMsg("rodando — acompanhe pelo histórico");
    setTimeout(load, 3000);
  };

  const togglePause = async (i: IntegrationRow) => {
    await api.post(`/api/t/${slug}/integrations/${i.id}/${i.status === "paused" ? "resume" : "pause"}`);
    load();
  };

  const remove = async (i: IntegrationRow) => {
    if (!confirm(`Apagar integração ${i.type}? Os dados já sincronizados ficam.`)) return;
    await api.del(`/api/t/${slug}/integrations/${i.id}`);
    load();
  };

  const backfillAll = async () => {
    if (!confirm("Disparar backfill de 365 dias em todas as integrações ativas?")) return;
    await api.post(`/api/t/${slug}/integrations/backfill`);
    setMsg("backfill iniciado em background — pode levar horas");
  };

  if (!list) return <div className="center-loading">carregando…</div>;

  const notConfigured = list.available.filter((t) => !list.configured.find((c) => c.type === t));

  return (
    <>
      <div className="section-head">
        <div>
          <p className="eyebrow">Admin</p>
          <h1>Integrações</h1>
        </div>
        <button className="btn small accent" onClick={backfillAll}>Backfill 365d</button>
      </div>

      {msg && <p style={{ color: msg.startsWith("erro") ? "var(--red)" : "var(--green)", fontSize: ".88rem" }}>{msg}</p>}

      <div className="card" style={{ marginBottom: "1.5rem" }}>
        <h3>Configuradas</h3>
        {list.configured.length === 0 ? (
          <p style={{ color: "var(--text-muted)" }}>nenhuma ainda.</p>
        ) : (
          <table className="simple">
            <thead><tr><th></th><th>Tipo</th><th>Último sucesso</th><th>Erro</th><th></th></tr></thead>
            <tbody>
              {list.configured.map((i) => (
                <tr key={i.id}>
                  <td><StatusDot status={i.status} /></td>
                  <td><strong>{TYPE_LABEL[i.type] ?? i.type}</strong></td>
                  <td className="mono" style={{ fontSize: ".82rem" }}>{fmtDateTime(i.last_success_at)}</td>
                  <td style={{ color: "var(--red)", fontSize: ".82rem" }}>{i.last_error ?? "—"}</td>
                  <td style={{ display: "flex", gap: ".4rem" }}>
                    <button className="btn small secondary" onClick={() => runNow(i.id)}>Rodar</button>
                    <button className="btn small secondary" onClick={() => togglePause(i)}>{i.status === "paused" ? "Retomar" : "Pausar"}</button>
                    <button className="btn small secondary" onClick={() => { setEditing(i.type); setForm({}); }}>Reconfigurar</button>
                    <button className="btn small secondary" style={{ color: "var(--red)", borderColor: "var(--red)" }} onClick={() => remove(i)}>Apagar</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {notConfigured.length > 0 && (
        <div className="card" style={{ marginBottom: "1.5rem" }}>
          <h3>Adicionar nova</h3>
          <div style={{ display: "flex", gap: ".5rem", flexWrap: "wrap" }}>
            {notConfigured.map((t) => (
              <button key={t} className="btn small secondary" onClick={() => { setEditing(t); setForm({}); }}>
                + {TYPE_LABEL[t] ?? t}
              </button>
            ))}
          </div>
        </div>
      )}

      {editing && (
        <div className="card">
          <h3>Configurar {TYPE_LABEL[editing] ?? editing}</h3>
          <form className="form" style={{ maxWidth: 540 }} onSubmit={onSubmit}>
            {editing === "erp" && (
              <label>
                Provedor de ERP
                <select
                  value={form.provider || "omie"}
                  onChange={(e) => setForm({ provider: e.target.value })}
                >
                  {ERP_PROVIDERS.map((p) => (
                    <option key={p.value} value={p.value}>{p.label}</option>
                  ))}
                </select>
              </label>
            )}
            {fieldsFor(editing, form).map((f) => (
              <label key={f.key}>
                {f.label}
                {f.hint && <span style={{ fontSize: ".75rem", color: "var(--text-muted)", marginLeft: ".3rem" }}>· {f.hint}</span>}
                <input
                  type={f.type ?? "text"}
                  value={form[f.key] ?? ""}
                  onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                  autoComplete="off"
                />
              </label>
            ))}
            <div style={{ display: "flex", gap: ".5rem" }}>
              <button className="btn" type="submit" disabled={busy}>{busy ? "Salvando…" : "Salvar"}</button>
              <button type="button" className="btn secondary" onClick={() => { setEditing(null); setForm({}); }}>Cancelar</button>
            </div>
          </form>
        </div>
      )}
    </>
  );
}
